// utils/activityHelpers.js
// Helper functions for activity processing — includes campaign company enrichment

// ─────────────────────────────────────────────────────────────────────────────
// Display name resolution
// ─────────────────────────────────────────────────────────────────────────────

export function getCleanDisplayName(activity) {
  const { display_name, creator_id, username, user_id, is_private } = activity;

  if (is_private || display_name === "Anonymous User") return "Anonymous User";

  if (isCreatorActivity(activity)) {
    return display_name || creator_id || username || "Creator";
  }

  if (user_id && display_name) {
    if (display_name.includes("@") || display_name === "A New User") return "User";
    return display_name;
  }

  return display_name || "User";
}

export function getReplyDisplayName(reply) {
  if (reply.is_private) return "Anonymous User";

  const author       = reply.author || {};
  const display_name = reply.display_name || author.display_name;

  if (author.type === "creator" && display_name) return display_name;

  if (author.type === "normal") {
    if (author.first_name || author.last_name)
      return `${author.first_name || ""} ${author.last_name || ""}`.trim();
    if (display_name && display_name.includes("@")) return "User";
    if (display_name && display_name !== "A New User") return display_name;
    return "User";
  }

  return (
    display_name ||
    author.username ||
    `${author.first_name || ""} ${author.last_name || ""}`.trim() ||
    "Unknown User"
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Creator identification
// ─────────────────────────────────────────────────────────────────────────────

export function getCreatorIdentifier(activity) {
  const { creator_id, username, creator_user, display_name, activity_type } = activity;

  if (creator_id)                      return creator_id;
  if (creator_user?.username)          return creator_user.username;
  if (creator_user?.creator?.username) return creator_user.creator.username;
  if (username)                        return username;

  if (
    (activity_type === "Post" || activity_type === "Achievement") &&
    display_name &&
    display_name !== "A New User" &&
    display_name !== "Anonymous User"
  ) {
    return display_name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  return null;
}

export function isCreatorActivity(activity) {
  const { creator_id, creator_user, username, user_id, activity_type } = activity;
  if (creator_id || creator_user || username) return true;
  if ((activity_type === "Post" || activity_type === "Achievement") && !user_id) return true;
  if ((creator_id || creator_user || username) && user_id) return true;
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Campaign company enrichment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns { name, photo } for a campaign item when the backend resolved a
 * single company from campaign.shared_with. Returns null otherwise.
 */
export function getCampaignCompany(activity) {
  if (activity.activity_type !== "Campaign") return null;
  const name = activity.campaign_company_name;
  if (!name) return null;
  return { name, photo: activity.campaign_company_photo || null };
}

export function hasCampaignCompany(activity) {
  return getCampaignCompany(activity) !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Activity accent colour — used by ActivitySummary & FeedItem for theming
// ─────────────────────────────────────────────────────────────────────────────

const ACCENT_COLORS = {
  Post:        "#3b82f6",
  Campaign:    "#8b5cf6",
  Achievement: "#f59e0b",
  CastingCall: "#10b981",
  Payout:      "#10b981",
  Partnership: "#3b82f6",
  Join:        "#8b5cf6",
  Payment:     "#10b981",
  Pitch:       "#3b82f6",
  Reply:       "#6b7280",
  User:        "#8b5cf6",
};

export function getActivityAccentColor(activityType) {
  return ACCENT_COLORS[activityType] || "#6b7280";
}

// ─────────────────────────────────────────────────────────────────────────────
// Activity phrase builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a human-readable verb phrase for any activity type.
 * Campaign company name is deliberately NOT appended here — ActivitySummary
 * renders it as a separate visual badge with its own logo and styling.
 */
export function getActivityPhrase(activity) {
  const { activity_type, activity_name } = activity;
  const type = activity_type?.trim();

  const phrases = {
    Post:        activity_name,
    Campaign:    `launched "${activity_name}"`,
    Pitch:       `pitched for ${activity_name}`,
    Payout:      "received a payout",
    Partnership: "started a new partnership",
    Join:        "joined Blitz",
    User:        "just joined Blitz",
    Payment:     "sent a BlitzPay",
    Achievement: activity_name,
    Reply:       activity_name,
    CastingCall: activity_name,
  };

  return phrases[type] ?? activity_name;
}

// ─────────────────────────────────────────────────────────────────────────────
// Relative timestamp
// ─────────────────────────────────────────────────────────────────────────────

export function getRelativeTime(dateStr) {
  if (!dateStr) return "";
  const now     = new Date();
  const date    = new Date(dateStr);
  const diffMs  = now - date;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60)  return `${diffSec}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60)  return `${diffMin}m`;
  const diffHr  = Math.floor(diffMin / 60);
  if (diffHr < 24)   return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7)   return `${diffDay}d`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile link resolver
// ─────────────────────────────────────────────────────────────────────────────

export function getProfileLink(activity) {
  const { is_private, user_id } = activity;
  if (is_private) return null;

  if (isCreatorActivity(activity)) {
    const identifier = getCreatorIdentifier(activity);
    return identifier ? `/profile/${identifier}` : null;
  }

  return user_id ? `/profile/${user_id}` : null;
}